export interface ReactAppAPI {
  unmount: () => void;
  navigate?: (path: string, options?: any) => void;
}

export interface LoadReactAppOptions {
  isProduction: boolean;
  devServerUrl?: string;
}

export async function loadReactApp(
  container: HTMLElement,
  basePath = '/',
  options?: LoadReactAppOptions
): Promise<ReactAppAPI> {
  if ((window as any).renderReactApp) {
    console.log('React app already loaded, mounting...');

    return (window as any).renderReactApp(container, basePath);
  }

  const isProduction = options?.isProduction ?? false;
  const devServerUrl = options?.devServerUrl || '';

  if (isProduction) {
    console.log('Production mode: Loading React app from static assets');

    return loadReactAppFromAssets(container, basePath);
  } else {
    console.log(`Development mode: Loading React app from: ${devServerUrl}`);

    return loadReactAppFromDevServer(container, basePath, devServerUrl);
  }
}

async function loadReactAppFromDevServer(
  container: HTMLElement,
  basePath: string,
  devServerUrl: string
): Promise<ReactAppAPI> {
  try {
    await loadScript(`${devServerUrl}/main.js`);

    // Wait for renderApp to be available
    await waitForGlobal('renderReactApp', 5000);

    if ((window as any).renderReactApp) {
      console.log('React app loaded successfully, mounting in container...');

      return (window as any).renderReactApp(container, basePath);
    }

    throw new Error('Failed to load React app - renderApp function not found');
  } catch (error) {
    console.error('Error loading React scripts:', error);
    throw error;
  }
}

async function loadReactAppFromAssets(
  container: HTMLElement,
  basePath: string
): Promise<ReactAppAPI> {
  try {
    await loadScript('/react/main.js');

    await waitForGlobal('renderReactApp', 5000);

    if ((window as any).renderReactApp) {
      console.log('React app loaded from static assets, mounting...');

      return (window as any).renderReactApp(container, basePath);
    }

    throw new Error('Failed to load React app - renderApp function not found');
  } catch (error) {
    console.error('Error loading React app from static assets:', error);
    throw error;
  }
}

async function loadScript(src: string): Promise<void> {
  const existingScript = document.querySelector(`script[src="${src}"]`);

  if (existingScript) {
    console.log(`✓ Script already loaded: ${src}`);

    return;
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.src = src;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      resolve();
    };

    script.onerror = () => {
      reject(new Error(`Failed to load script: ${src}`));
    };

    document.head.appendChild(script);
  });
}

function waitForGlobal(
  globalName: string,
  timeout = 5000
): Promise<void> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const interval = 100;

    const check = () => {
      if ((window as any)[globalName]) {
        resolve();
      } else if (Date.now() - startTime > timeout) {
        reject(
          new Error(
            `Timeout waiting for ${globalName} to be available after ${timeout}ms`
          )
        );
      } else {
        setTimeout(check, interval);
      }
    };

    check();
  });
}

